import { Box, Flex, IconButtonProps } from '@chakra-ui/react';
import Action, { ActionLoadingCallback } from 'components/DataTable/Actions/Action';
import datatableActions from 'constants/datatableActions';

type DatatableActionKeys = keyof typeof datatableActions;

type StringRenderer<T> = (data: T) => string;

export type CustomSelectParams<T extends object> = {
  ids?: string[];
  selected?: Record<string, T>;
};

export type ActionCallbackItem<T extends object> = T & CustomSelectParams<T>;

export type IAction<T extends object> = {
  type: string;
  title?: string;
  loading?: boolean;
  callback: (
    callbackItem: ActionCallbackItem<T>,
    loadingCb: ActionLoadingCallback
  ) =>
    | void
    | ((data: T, item: T, cb: ActionLoadingCallback) => void)
    | ((inlineItem: IAction<T>, inlineCb: ActionLoadingCallback) => void)
    | Promise<void>;
  condition?: (data: T) => boolean;
  renderTitle?: StringRenderer<T>;
  renderMessage?: StringRenderer<T>;
  icon?: React.ElementType;
  actionProps?: Omit<IconButtonProps, 'aria-label'>;
};

export type ActionCallback<T extends object> = (item: IAction<T>, cb: ActionLoadingCallback) => void;

export type ActionsCallback<T extends object> = (data: T, cb: ActionLoadingCallback) => void;

type ActionsProps<T extends object> = {
  items: IAction<T>[];
  callbacks: Record<string, (callback: IAction<T>['callback'], item: IAction<T>, cb: ActionLoadingCallback) => void>;
  filtering?: boolean;
  loading?: boolean;
};

function Actions<T extends object>({ items, callbacks, filtering, loading: datatableLoading }: ActionsProps<T>) {
  const renderItems = items.map(({ loading, actionProps, ...item }) => (
    <Box key={item.type}>
      <Action
        loading={loading}
        callback={(cb) => {
          if (loading) return;

          const fn = callbacks[item.type]
            ? () => callbacks[item.type](item.callback, item, cb)
            : () => (item.callback as (callbackItem: T, loadingCb: ActionLoadingCallback) => void)(item as T, cb);
          fn();
        }}
        disabled={datatableLoading}
        title={item.title ?? datatableActions[item.type as DatatableActionKeys].title}
        filtering={filtering && item.type === 'filter'}
        iconComponent={item.icon ?? datatableActions[item.type as DatatableActionKeys].icon}
        {...actionProps}
      />
    </Box>
  ));

  return (
    <Flex gap={2} justifyContent="flex-end">
      {renderItems}
    </Flex>
  );
}

export default Actions;
